import { useState, useMemo, type FormEvent } from "react";
import { Lock, Mail, Eye, EyeOff, ArrowRight, AlertCircle, Loader2 } from "lucide-react";
import { WolfLogo } from "../components/WolfLogo";
import { loginWithPassword } from "../api";
import { getClientLogoUrl } from "../components/social/socialBrand";

export function LoginPage() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const tenantInfo = useMemo(() => {
    const host = window.location.hostname.toLowerCase();
    if (host.startsWith("meds.")) {
      return {
        brandName: "Clínica MEDS",
        subtitle: "Portal de Clientes · Dashboard y Respuestas Sociales",
      };
    }
    if (host.startsWith("chp.")) {
      return {
        brandName: "Hospital del Profesor",
        subtitle: "Portal de Clientes · Dashboard y Respuestas Sociales",
      };
    }
    if (host.startsWith("morapavic.")) {
      return {
        brandName: "Mora Pavic Odontología",
        subtitle: "Portal de Clientes · Dashboard y Respuestas Sociales",
      };
    }
    return {
      brandName: "Wolf Control Room",
      subtitle: "Portal Central de Inteligencia y Operaciones",
    };
  }, []);

  const tenantLogoUrl = useMemo(() => {
    const slug = window.location.hostname.toLowerCase().split(".")[0];
    return getClientLogoUrl(slug);
  }, []);
  const [logoFailed, setLogoFailed] = useState(false);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!email.trim() || !password) {
      setError("Por favor ingresa tu correo corporativo y contraseña.");
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const res = await loginWithPassword(email.trim(), password);
      if (res.success) {
        window.location.assign(res.redirect_url || "/");
      } else {
        setError("No fue posible validar las credenciales.");
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : "Error al iniciar sesión.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="login-page">
      <div className="login-card">
        <div className="login-header">
          {tenantLogoUrl && !logoFailed ? (
            <div className="login-header-brands">
              <div
                className="login-brand-logo"
                style={
                  window.location.hostname.toLowerCase().startsWith("morapavic.")
                    ? { backgroundColor: "rgba(7, 160, 174, .6)" }
                    : undefined
                }
              >
                <img
                  src={tenantLogoUrl}
                  alt={tenantInfo.brandName}
                  onError={() => setLogoFailed(true)}
                />
              </div>
              <div className="login-logo">
                <WolfLogo height={56} />
              </div>
            </div>
          ) : (
            <>
              <div className="login-logo">
                <WolfLogo height={32} />
              </div>
              <h1 className="login-title">{tenantInfo.brandName}</h1>
            </>
          )}
          <p className="login-subtitle">{tenantInfo.subtitle}</p>
        </div>

        {error && (
          <div className="login-alert-error" role="alert" style={{ marginBottom: "16px" }}>
            <AlertCircle size={18} style={{ flexShrink: 0, marginTop: "2px" }} />
            <span>{error}</span>
          </div>
        )}

        <form className="login-form" onSubmit={handleSubmit}>
          <div className="login-field">
            <label htmlFor="login-email">Correo Corporativo</label>
            <div className="login-input-wrapper">
              <Mail size={16} className="login-input-icon" />
              <input
                id="login-email"
                type="email"
                className="login-input"
                placeholder="ejemplo@empresa.cl"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                autoComplete="username"
                autoFocus
                disabled={loading}
                required
              />
            </div>
          </div>

          <div className="login-field">
            <label htmlFor="login-password">Contraseña</label>
            <div className="login-input-wrapper">
              <Lock size={16} className="login-input-icon" />
              <input
                id="login-password"
                type={showPassword ? "text" : "password"}
                className="login-input"
                placeholder="••••••••••••"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                autoComplete="current-password"
                disabled={loading}
                required
              />
              <button
                type="button"
                className="login-input-toggle"
                onClick={() => setShowPassword(!showPassword)}
                title={showPassword ? "Ocultar contraseña" : "Ver contraseña"}
                tabIndex={-1}
              >
                {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
              </button>
            </div>
          </div>

          <button type="submit" className="login-submit-btn" disabled={loading}>
            {loading ? (
              <>
                <Loader2 size={16} className="spin" />
                <span>Iniciando sesión...</span>
              </>
            ) : (
              <>
                <span>Iniciar Sesión</span>
                <ArrowRight size={16} />
              </>
            )}
          </button>
        </form>

        <div className="login-divider">o continuar con</div>

        <a href="/auth/login" className="login-google-btn">
          <svg width="18" height="18" viewBox="0 0 24 24" aria-hidden="true">
            <path
              fill="#4285F4"
              d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
            />
            <path
              fill="#34A853"
              d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
            />
            <path
              fill="#FBBC05"
              d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.06H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.94l2.85-2.22.81-.63z"
            />
            <path
              fill="#EA4335"
              d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.06l3.66 2.84c.87-2.6 3.3-4.52 6.16-4.52z"
            />
          </svg>
          <span>Cuenta Google Workspace</span>
        </a>
      </div>
    </div>
  );
}
